<template>
  <div
    class="number-input-stepper"
    :class="{
      'number-input-stepper--compact': size === 'compact',
    }"
  >
    <label class="number-input-stepper__label body-s-high">
      <InputLabel
        v-if="label"
        class="spc-mb-1"
        :label="label"
        :required="required"
      />
      <div
        class="number-input-stepper__stepper"
        :class="{
          'number-input-stepper__stepper--error': error,
          'number-input-stepper__stepper--disabled': disabled
        }"
      >
        <input
          ref="stepperInput"
          class="number-input-stepper__input body-s-regular"
          :class="{
            'number-input-stepper__input--error': error,
            'number-input-stepper__input--compact': size === 'compact',
          }"
          :data-testid="`${id}_number_input`"
          :disabled="disabled"
          inputmode="decimal"
          :placeholder="placeholder"
          :readonly="readOnly"
          :step="step"
          type="number"
          :value="value"
          @blur="$emit('blur', $event)"
          @input="$emit('update:value', $event.target.value)"
        >
        <div class="number-input-stepper__controls">
          <button
            aria-label="Increment"
            class="number-input-stepper__button"
            :class="{
              'number-input-stepper__button--compact': size === 'compact',
            }"
            :disabled="disableIncrement"
            type="button"
            @click="increment"
            @mouseleave="incrementHovering = false"
            @mouseover="incrementHovering = true"
          >
            <BaseIcon
              class="number-input-stepper__icon"
              font-size="0.5rem"
              :icon="incrementIcon"
            />
          </button>
          <button
            aria-label="Decrement"
            class="number-input-stepper__button"
            :class="{
              'number-input-stepper__button--compact': size === 'compact',
            }"
            :disabled="disableDecrement"
            type="button"
            @click="decrement"
            @mouseleave="decrementHovering = false"
            @mouseover="decrementHovering = true"
          >
            <BaseIcon
              class="number-input-stepper__icon"
              font-size="0.5rem"
              :icon="decrementIcon"
            />
          </button>
        </div>
      </div>
    </label>
    <slot name="errors" />
  </div>
</template>

<script>
import InputLabel from './InputLabel.vue'
import BaseIcon from './BaseIcon.vue'

export default {
  name: 'NumberInputStepper',
  components: { InputLabel, BaseIcon },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '', 
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false, 
    },
    error: {
      type: Boolean,
      default: false, 
    },
    step: {
      type: [String, Number],
      default: '0', 
    },
    required: {
      type: Boolean,
      default: false, 
    },
    readOnly: {
      type: Boolean,
      default: false, 
    },
    id: {
      type: String,
      default: '', 
    },
    size: {
      type: String,
      default: 'comfortable',
      validator: (value) => ['comfortable', 'compact'].includes(value),
    },
    min: {
      type: [String, Number],
      default: null,
    },
    max: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['update:value', 'blur'],
  data() {
    return {
      incrementHovering: false,
      decrementHovering: false,
    }
  },
  computed: {
    disableIncrement() {
      return this.disabled || (this.maxPresent && (parseFloat(this.value) >= parseFloat(this.max)))
    },
    disableDecrement() {
      return this.disabled || (this.minPresent && (parseFloat(this.value) <= parseFloat(this.min)))
    },
    incrementIcon() {
      return this.incrementHovering ? 'fa-solid fa-chevron-up' : 'fa-regular fa-chevron-up'
    },
    decrementIcon() {
      return this.decrementHovering ? 'fa-solid fa-chevron-down' : 'fa-regular fa-chevron-down'
    },
    minPresent() {
      return this.min !== null && this.min !== undefined && this.min !== ''
    },
    maxPresent() {
      return this.max !== null && this.max !== undefined && this.max !== ''
    },
  },
  mounted() {
    const stepperInput = this.$refs.stepperInput
    if (!stepperInput) return

    stepperInput.addEventListener("keypress", this.preventNonNumericInput)
  },
  unmounted() {
    const stepperInput = this.$refs.stepperInput
    if (!stepperInput) return

    stepperInput.removeEventListener("keypress", this.preventNonNumericInput)
  },
  methods: {
    increment() {
      const newVal = (parseFloat(this.value) || 0) + parseFloat(this.step)
      if (this.maxPresent && (newVal > parseFloat(this.max))) {
        this.$emit('update:value', this.max)
        return
      }
      this.$emit('update:value', newVal)
    },
    decrement() {
      const newVal = (parseFloat(this.value) || 0) - parseFloat(this.step)
      if (this.minPresent && (newVal <= parseFloat(this.min))) {
        this.$emit('update:value', this.min)
        return
      }
      this.$emit('update:value', newVal)
    },
    preventNonNumericInput(event) {
      const key = event.key

      const validNonNumericKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter']

      if (!/^[0-9.-]$/.test(key) && !validNonNumericKeys.includes(key)) {
        event.preventDefault()
      }
    },
  },
}
</script>

<style scoped lang="scss">
.number-input-stepper {
  width: 4.4375rem;
  &__stepper {
    position: relative;
    display: flex;
    align-items: center;
    border: 1px solid var(--pepper-300);
    border-radius: var(--cr-br-small);
    background-color: var(--salt);
    transition: border-color 0.2s;
    &--disabled {
      color: var(--pepper-600);
      background-color: var(--pepper-200);
      pointer-events: none;
    }
    &:focus-within {
      border-color: var(--blueberry-1000);
    }
    &--error {
      border-color: var(--chicago-dog-red-1000);
    }
    &--error:focus-within {
      border-color: var(--chicago-dog-red-1000);
    }
  }

  &__input {
    flex: 1;
    border: none;
    background: transparent;
    padding: .65rem var(--space-2);
    padding-right: var(--space-6);
    width: 100%;
    outline: none;
    text-align: center;
    &--compact {
      padding-top: .25rem;
      padding-bottom: .25rem;
    }
    &--error {
      color: var(--chicago-dog-red-1000);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      display: none;
    }
    appearance: textfield;
    -moz-appearance: textfield;

    &::placeholder {
      color: var(--pepper-600);
    }
    &:disabled {
      color: var(--pepper-600);
      background-color: transparent;
    }
  }

  &__controls {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    border-left: 1px solid var(--pepper-300);
    border-top-right-radius: var(--cr-br-small);
    border-bottom-right-radius: var(--cr-br-small);
    overflow: hidden;
  }

  &__button {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0.1875rem 0.5rem;
    box-sizing: border-box;
    transition: background-color 0.2s;
    &--compact {
      padding: 0.125rem 0.5rem;
    }
    &:first-child {
      border-bottom: 1px solid var(--pepper-300);
      border-top-right-radius: calc(var(--cr-br-small) - 2px);
    }
    &:last-child {
      border-top: 1px solid var(--pepper-300);
      border-bottom-right-radius: calc(var(--cr-br-small) - 2px);
      margin-top: -1px;;
    }
    &:disabled {
      color: var(--pepper-600);
      background-color: var(--pepper-200);
      pointer-events: none;
    }
    &:hover {
      color: var(--pepper-1000);
    }
    &:focus-within {
      outline: 2px solid var(--blueberry-1000);
      outline-offset: -1px;
      z-index: 1;
    }
  }

  &__icon {
    color: var(--pepper-600);
    font-size: 0.5rem;
  }
}

.number-input-stepper :deep(.error-text:first-of-type) {
  margin-left: 0px;
  margin-top: 0px;
}
</style>
